import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  DeleteRecurringPaymentResponse,
  PaymentsServiceClientImpl,
  SetRefundBankAccountResponse,
} from '../../../grpc_client/grips/protos/payments/v1/payments_service';
import {
  useGripsClientContext,
  useGrpcClientChannel,
} from '../../sharedContexts/GrpcClientContext';
import {useUserSessionContext} from '../../sharedContexts/UserSessionContext';

import {grpc} from '@improbable-eng/grpc-web';
import {RecurringPaymentMethod} from '../../../grpc_client/grips/protos/payments/v1/payment_info';
import {Locale} from '../../../types/locale';
import {verifyAsCashOnDeliveryCountry} from '../../../utils/bank';
import {getGripsDevice} from '../../../utils/grpc/grpc-device';
import {useCache} from '../../../utils/hooks/useCache';
import {Log} from '../../../utils/logger/log';
import {createCacheIdentifier} from '../../sharedContexts/CacheInstanceContext';

export interface BankData {
  bic?: string;
  firstName: string;
  iban: string;
  lastName: string;
}
export interface CODPaymentInformation {
  id: string;
  bankData: BankData;
}
export type UpdateOrCreateBankInformation = (
  bankData: BankData,
) => Promise<SetRefundBankAccountResponse | undefined>;

const initialBankData = {
  bic: '',
  firstName: '',
  lastName: '',
  iban: '',
};

export type DeleteBankInformation = (
  paymentMethodId: string,
) => Promise<DeleteRecurringPaymentResponse | undefined>;

export interface CodErrorFormat {
  message: string;
  service: 'update' | 'delete' | 'fetch';
  statusCode: number;
}

export const COD_CACHE_PREFIX = 'paymentMethodCacheKey';

const codPaymentMethod = (paymentMethod: RecurringPaymentMethod) =>
  paymentMethod.type?.$case === 'bankPaymentMethod';

export const codPaymentInfoCacheIdentifier = createCacheIdentifier<
  string,
  CODPaymentInformation
>('codPaymentInformation');

export default function useCodPaymentInformation(
  locale: Locale,
  isOnBankDataPage = false,
): {
  data: {
    codPaymentInformation?: CODPaymentInformation;
    loadingCache: boolean;
    error?: CodErrorFormat;
  };
  fetchCodPaymentInformation: () => void;
  updateOrCreateBankInformation: UpdateOrCreateBankInformation;
} {
  const loading = useRef(false);
  const {userId} = useUserSessionContext();
  const COD_CACHE_KEY = `${COD_CACHE_PREFIX}-${userId}`;
  const isCODAvailableForLocale =
    verifyAsCashOnDeliveryCountry(locale) || isOnBankDataPage;
  const [error, setError] = useState<CodErrorFormat>();
  const {childShopId, device, shopId} = useGripsClientContext();
  const channel = useGrpcClientChannel();
  const {apiToken} = useUserSessionContext();
  const client = useMemo(
    () => new PaymentsServiceClientImpl(channel),
    [channel],
  );
  const codPaymentInfoCache = useCache<string, CODPaymentInformation>(
    codPaymentInfoCacheIdentifier,
  );
  const [codPaymentData, setCodPaymentData] = useState(
    codPaymentInfoCache.get(COD_CACHE_KEY),
  );
  const [loadingData, setLoadingData] = useState(
    !codPaymentInfoCache.get(COD_CACHE_KEY),
  );

  useEffect(() => {
    const listener = () => {
      setCodPaymentData(codPaymentInfoCache.get(COD_CACHE_KEY));
    };
    codPaymentInfoCache.addListener(listener);

    return () => {
      codPaymentInfoCache.removeListener(listener);
    };
  }, [setCodPaymentData, codPaymentInfoCache, COD_CACHE_KEY]);

  const fetchCodPaymentInformation = useCallback(async () => {
    let canFetchCodPaymentInfo = true;

    if (!userId) {
      canFetchCodPaymentInfo = false;
    }

    if (!isCODAvailableForLocale) {
      canFetchCodPaymentInfo = false;
    }

    if (!apiToken) {
      canFetchCodPaymentInfo = false;
    }

    if (codPaymentInfoCache.get(COD_CACHE_KEY)) {
      canFetchCodPaymentInfo = false;
    }

    if (loading.current) {
      canFetchCodPaymentInfo = false;
    }

    if (!canFetchCodPaymentInfo) {
      setLoadingData(false);
      loading.current = false;
      return;
    }

    setLoadingData(true);
    loading.current = true;

    try {
      const response = await client.getRecurringPaymentMethods({
        childShopId,
        session: {authToken: apiToken},
        device: getGripsDevice(device),
      });

      const paymentMethod = mapToPaymentInformation(
        response.paymentInformation.find(codPaymentMethod),
      );

      if (paymentMethod) {
        codPaymentInfoCache.set(COD_CACHE_KEY, paymentMethod);
      }

      setError(undefined);
    } catch (err) {
      if (err.code === grpc.Code.Unauthenticated) {
        console.error(err.message);
      } else {
        setError({
          message:
            err.code === grpc.Code.NotFound
              ? 'no recurring payment method found'
              : err.message,
          service: 'fetch',
          statusCode: err.code,
        });
      }
    } finally {
      setLoadingData(false);
      loading.current = false;
    }
  }, [
    isCODAvailableForLocale,
    COD_CACHE_KEY,
    client,
    childShopId,
    device,
    userId,
  ]);

  const updateLoading = useRef(false);

  const updateOrCreateBankInformation = useCallback(
    async (
      updateFields: BankData,
    ): Promise<SetRefundBankAccountResponse | undefined> => {
      if (updateLoading.current) {
        return;
      }
      updateLoading.current = true;
      try {
        const recurringPaymentMethods = await client.setRefundBankAccount({
          config: {
            shopId,
            childShopId,
            device: getGripsDevice(device),
            session: {
              $case: 'customerSession',
              customerSession: {
                authToken: apiToken,
              },
            },
          },
          bankAccount: {
            ...updateFields,
          },
        });

        const paymentMethod = mapToPaymentInformation(
          recurringPaymentMethods.paymentMethods.find(codPaymentMethod),
        );

        if (paymentMethod) {
          codPaymentInfoCache.set(COD_CACHE_KEY, paymentMethod);
        }

        return recurringPaymentMethods;
      } catch (err) {
        setError({
          message: err.message,
          service: 'update',
          statusCode: err.code,
        });
        throw err;
      } finally {
        updateLoading.current = false;
      }
    },
    [
      client,
      shopId,
      childShopId,
      device,
      codPaymentInfoCache,
      COD_CACHE_KEY,
      apiToken,
    ],
  );

  useEffect(() => {
    if (error) {
      Log.error(error.message, {
        level: error.service === 'update' ? 'fatal' : 'error',
        statusCode: error.statusCode,
        error: new Error(error.message),
        additionalData: {
          service: `GRPC ${error.service} recurring payment failed`,
        },
      });
    }
  }, [error]);

  return {
    data: {
      codPaymentInformation: codPaymentData,
      loadingCache: loadingData,
      error,
    },
    fetchCodPaymentInformation,
    updateOrCreateBankInformation,
  };
}

function mapToPaymentInformation(
  paymentMethod: RecurringPaymentMethod | undefined,
): CODPaymentInformation | undefined {
  if (!paymentMethod) {
    return undefined;
  }

  return {
    id: paymentMethod.id,
    bankData: {
      ...(paymentMethod.type?.bankPaymentMethod.bankAccount || initialBankData),
    },
  };
}
